/**
 * Sign in.
 *
 * One job, one primary action. The form handles the first-sign-in case Cognito
 * forces on a newly created account, because otherwise the very first thing a
 * new user sees is an unexplained failure.
 */

import { useState, type FormEvent } from 'react';
import type { CognitoUser } from 'amazon-cognito-identity-js';
import { Button, Field, Input } from '../components/ui';
import { BrandMark } from '../components/icons';
import { useAuth } from './AuthProvider';
import { NewPasswordRequiredError, completeNewPassword, SignInError } from './cognito';
import './SignInPage.css';

type Stage = { name: 'credentials' } | { name: 'newPassword'; user: CognitoUser; email: string };

export function SignInPage() {
  const { signIn, adoptSession } = useAuth();
  const [stage, setStage] = useState<Stage>({ name: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmitCredentials(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email.trim(), password);
    } catch (caught) {
      if (caught instanceof NewPasswordRequiredError) {
        setStage({ name: 'newPassword', user: caught.user, email: email.trim() });
        setPassword('');
      } else if (caught instanceof SignInError) {
        setError(caught.message);
      } else {
        setError('Sign-in failed. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitNewPassword(event: FormEvent) {
    event.preventDefault();
    if (stage.name !== 'newPassword') return;
    setError(null);
    setBusy(true);
    try {
      adoptSession(await completeNewPassword(stage.user, newPassword, stage.email));
    } catch (caught) {
      setError(caught instanceof SignInError ? caught.message : 'Could not set that password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="signin">
      <div className="signin__card">
        <div className="signin__brand">
          <BrandMark width={32} height={32} style={{ color: 'var(--color-action)' }} />
          <span className="signin__wordmark">AlohaMula</span>
        </div>

        {stage.name === 'credentials' ? (
          <form className="signin__form" onSubmit={onSubmitCredentials}>
            <div>
              <h1 className="signin__title">Sign in</h1>
              <p className="signin__subtitle">Your envelopes, bills, and ledger.</p>
            </div>

            <Field label="Email">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="email"
                  name="email"
                  autoComplete="username"
                  required
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              )}
            </Field>

            <Field label="Password">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              )}
            </Field>

            {error && (
              <p className="signin__error" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" emphasis="primary" block loading={busy}>
              Sign in
            </Button>
          </form>
        ) : (
          <form className="signin__form" onSubmit={onSubmitNewPassword}>
            <div>
              <h1 className="signin__title">Choose a password</h1>
              <p className="signin__subtitle">
                This account was created for you, so pick your own password to finish.
              </p>
            </div>

            <Field
              label="New password"
              hint="At least 12 characters, with an uppercase letter, a lowercase letter, and a number."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  autoFocus
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                />
              )}
            </Field>

            {error && (
              <p className="signin__error" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" emphasis="primary" block loading={busy}>
              Set password and sign in
            </Button>
          </form>
        )}
      </div>
    </main>
  );
}
